import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../core/models/entities/Order";
import { Dialog_VM, DialogFrame } from "../../../components/Dialogs/dialog-base";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../components/Toast/Toast";
import { formatMoney } from "../../admin-api-keys/shared/formatters";
import { formatDate } from "../../../../utils/functions/DateUtil";
import { parseUtcStamp } from "../../../../utils/functions/storageUpdatedAt";
import {
  OrderPriceAdjustmentDto,
  OrderPriceAdjustmentLineDto,
  OrderPriceAdjustmentResultDto,
} from "../../../../data/models/responses/OrderPriceAdjustmentDto";
import { VALIDATION_ERRORS } from "../../../../data/models/mappers/mapPriceAdjustmentError";
import {
  PriceDraft,
  computeTotals,
  effectivePrice,
  evaluateDraft,
  formatPriceInput,
  hasAdjustableAdditionalFee,
  initialDraft,
  notAdjustableKey,
  parsePriceInput,
  round2,
} from "./adjustPrices";

export const NOTE_MAX_LENGTH = 1000;

interface AdjustPricesDialogProps {
  dialogVm: Dialog_VM;
  // The row the admin clicked; null while nothing is being adjusted.
  order: Order | null;
  onClose: () => void;
}

const inputClass =
  "w-36 rounded border border-[#C3CAD9] bg-white px-2 py-1 text-right text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-[#3CB44A] disabled:bg-[#FAFBFE] disabled:text-[#8A93A6]";
const invalidInputClass = " !border-red-500 !ring-red-300";
// An edit that is not saved yet (differs from the stored agreement) - the
// same orange as the changed totals in the Before -> After block.
const changedInputClass = " !border-orange-500 !ring-orange-300";
// The field shows a stored agreement unchanged.
const agreedInputClass = " font-semibold";

// Admin "Adjust prices" for an "Awaiting payment" order (flag
// REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED). 1 GET at open, 1 PUT at save -
// no NAV call, no price lookup. "Our price" stays as documentation; the
// agreed price per line is what the new Stripe link, the mail and (at
// payment) NAV get. Totals are previewed with the same formula the API uses
// (adjustPrices.ts); the API's answer is the fact.
export function AdjustPricesDialog({
  dialogVm,
  order,
  onClose,
}: AdjustPricesDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { orderRepository } = useRepositoriesContext();
  const query = orderRepository.useOrderPriceAdjustment(
    order?.id,
    dialogVm.isOpen && !!order,
  );
  const data = query.data;

  const [draft, setDraft] = useState<PriceDraft>({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // A PUT whose answer never arrived (transport failure). The API may have
  // committed it - new link and mail included - so the next 409 from this
  // dialog is most likely our own save, not another admin's.
  const [saveUnconfirmed, setSaveUnconfirmed] = useState(false);

  // Fresh data (open, refetch after a version conflict) resets the draft to
  // the stored agreed prices - the version inside is the PUT's ETag.
  useEffect(() => {
    if (!data) return;
    setDraft(initialDraft(data.lines));
    setNote("");
    setSaveUnconfirmed(false);
  }, [data]);

  const evaluation = useMemo(
    () => (data ? evaluateDraft(data.lines, draft) : undefined),
    [data, draft],
  );
  const after = useMemo(
    () =>
      data && evaluation
        ? computeTotals(evaluation.effectiveLines, data.vatRate)
        : undefined,
    [data, evaluation],
  );

  // A read in flight counts as loading even when stale data is on screen:
  // after a save the query is marked stale without a refetch (repository,
  // FAST REGEL), so a re-open of the same order first shows the OLD prices
  // and the OLD version until the fresh GET lands - a save in that window
  // would only earn a 409. Same window during the refetch after a version
  // conflict. So fields and Save wait for the fresh data.
  const fetching = query.isFetching;
  const loading = query.isLoading || fetching;
  const busy = saving || fetching;

  const canSave =
    !!data &&
    data.adjustable &&
    !!evaluation &&
    evaluation.hasChange &&
    evaluation.isValid &&
    !busy;

  function close() {
    // Reset so a re-open of the same order (same cached data reference, so
    // the effect above would not fire) starts from the stored prices.
    setDraft(data ? initialDraft(data.lines) : {});
    setNote("");
    setSaveUnconfirmed(false);
    dialogVm.closeDialog();
    onClose();
  }

  // The frame's X button closes the frame itself (closeDialog, then onClose)
  // before this component gets a say, so it is guarded here - like the
  // Cancel button - while a PUT is in flight: closing mid-save would set
  // order to null under the answer (toasts after the dialog is gone, and a
  // 409's refetch would hit the now-empty key = one wasted GET).
  const frameVm: Dialog_VM = {
    ...dialogVm,
    closeDialog: () => {
      if (!saving) dialogVm.closeDialog();
    },
  };
  function closeFromFrame(): void {
    if (!saving) close();
  }

  const confirm = useConfirmationDialog(
    t("orderPriceAdjustment.confirm", {
      orderNumber: data?.orderNumber ?? order?.orderNumber ?? "",
      total: after ? formatMoney(round2(after.total), data?.currency) : "",
    }),
    () => void save(),
  );

  async function save(): Promise<void> {
    if (!data || !evaluation || !canSave) return;
    setSaving(true);
    let result: OrderPriceAdjustmentResultDto;
    try {
      result = await orderRepository.adjustOrderPrices(data.orderId, {
        expectedVersion: data.version,
        note: note.trim() === "" ? undefined : note.trim(),
        lines: evaluation.changes,
      });
    } catch {
      // Transport failure / unknown answer: the PUT may or may not have been
      // committed (a timeout after the API's commit means the new link and
      // mail are already out). Keep the dialog and the typed prices open,
      // tell the admin to reload before trying again, and remember it so a
      // 409 on the next attempt is not blamed on "someone else".
      setSaveUnconfirmed(true);
      showErrorToast(t("orderPriceAdjustment.saveFailed"));
      setSaving(false);
      return;
    }
    setSaving(false);
    handleResult(result);
  }

  function handleResult(result: OrderPriceAdjustmentResultDto): void {
    const orderNumber = data?.orderNumber ?? "";
    switch (result.outcome) {
      case "adjusted":
        showSuccessToast(t("orderPriceAdjustment.success", { orderNumber }));
        if (result.oldLinkKilled === false) {
          showInfoToast(t("orderPriceAdjustment.oldLinkNotKilled"));
        }
        if (result.mailSent === false) {
          showInfoToast(t("orderPriceAdjustment.mailNotSent"));
        }
        close();
        return;
      case "versionConflict":
        // The order moved on since our GET: reload (the effect above resets
        // the draft to the stored prices) and let the admin look again.
        // After an unconfirmed save of our own the version bump is most
        // likely that save - say "reloaded", not "changed by someone else".
        showInfoToast(
          t(
            saveUnconfirmed
              ? "orderPriceAdjustment.reloadedAfterUnconfirmedSave"
              : "orderPriceAdjustment.versionConflict",
          ),
        );
        void query.refetch();
        return;
      case "notAdjustable":
        showErrorToast(
          t("orderPriceAdjustment.notAdjustable", {
            orderNumber,
            reason: reasonText(result.reason ?? result.currentStatus ?? ""),
          }),
        );
        close();
        return;
      case "paidOnOldLink":
        showErrorToast(t("orderPriceAdjustment.paidOnOldLink", { orderNumber }));
        close();
        return;
      case "stripeUnavailable":
        showErrorToast(t("orderPriceAdjustment.stripeUnavailable"));
        return;
      case "validationError":
        if (result.error === "noChange") {
          showInfoToast(t("orderPriceAdjustment.noChange"));
        } else {
          // stripeRejected carries Stripe's own message in `detail` (e.g.
          // the amount is below the currency minimum): shown after the
          // translated code, so the admin sees what to change. The dialog
          // stays open with the typed prices - nothing was written.
          const text = errorText(result.error ?? "");
          showErrorToast(
            t("orderPriceAdjustment.validationError", {
              error: result.detail ? `${text}: ${result.detail}` : text,
            }),
          );
        }
        return;
    }
  }

  // The API's 400 codes are data; the known ones render translated, an
  // unknown code (or a plain message from a newer API) renders as-is.
  function errorText(error: string): string {
    return VALIDATION_ERRORS.includes(error)
      ? t(`orderPriceAdjustment.error.${error}`)
      : error;
  }

  // The API's reason codes are data; only the rendering is translated, and
  // an unknown code renders as-is.
  function reasonText(reason: string): string {
    const known = [
      "notAwaitingPayment",
      "paymentStatus",
      "orgTypeNotSupported",
      "noStripeSession",
      "paidOnOldLink",
    ];
    return known.includes(reason)
      ? t(`orderPriceAdjustment.reason.${reason}`)
      : reason;
  }

  if (!order) return null;

  return (
    <>
      {/* The X button must go through close() too: it resets the draft, so
          an abandoned edit cannot resurface as if it were a saved agreement
          when the same order is re-opened (same cached data reference, so
          the reset effect would not fire). */}
      <DialogFrame dialogOption={frameVm} onClose={closeFromFrame}>
        <div
          className={"flex flex-col space-y-4 max-w-[900px] w-[80vw]"}
          data-testid="adjust-prices-dialog"
        >
          <h2 className={"text-lg font-bold pr-8"}>
            {t("orderPriceAdjustment.title", {
              orderNumber: data?.orderNumber ?? order.orderNumber,
            })}
          </h2>
          {loading && (
            <p
              className={"text-sm text-gray-600"}
              data-testid="adjust-prices-loading"
            >
              {t("orderPriceAdjustment.loading")}
            </p>
          )}
          {query.isError && (
            <p className={"text-sm text-red-700"} data-testid="adjust-prices-error">
              {t("orderPriceAdjustment.loadFailed")}
            </p>
          )}
          {data && (
            <>
              <p className={"text-sm text-gray-600"}>
                {data.organizationName} · {data.status}
              </p>
              {!data.adjustable && (
                <p
                  className={
                    "rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-700"
                  }
                  data-testid="adjust-prices-not-adjustable"
                >
                  {t("orderPriceAdjustment.notAdjustable", {
                    orderNumber: data.orderNumber,
                    reason: reasonText(data.notAdjustableReason ?? ""),
                  })}
                </p>
              )}
              <div className={"max-h-[50vh] overflow-y-auto"}>
                <table className={"w-full text-sm border-collapse"}>
                  <thead className={"uppercase text-xs text-gray-600"}>
                    <tr>
                      <th className={"text-left px-2 py-1"}>
                        {t("orderPriceAdjustment.line")}
                      </th>
                      <th className={"text-right px-2 py-1"}>
                        {t("orderPriceAdjustment.qty")}
                      </th>
                      <th className={"text-right px-2 py-1"}>
                        {t("orderPriceAdjustment.ourPrice")}
                      </th>
                      <th className={"text-right px-2 py-1"}>
                        {t("orderPriceAdjustment.agreedPrice")}
                      </th>
                      <th className={"text-right px-2 py-1"}>
                        {t("orderPriceAdjustment.lineTotal")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((line) => (
                      <PriceLineRow
                        key={line.lineId}
                        line={line}
                        currency={data.currency}
                        raw={
                          draft[line.lineId] ??
                          (line.agreedPrice === null ||
                          line.agreedPrice === undefined
                            ? ""
                            : formatPriceInput(line.agreedPrice))
                        }
                        changed={
                          !!evaluation &&
                          evaluation.changes.some((c) => c.lineId === line.lineId)
                        }
                        disabled={!data.adjustable || busy}
                        onChange={(value) =>
                          setDraft((d) => ({ ...d, [line.lineId]: value }))
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {hasAdjustableAdditionalFee(data.lines) && (
                <p className={"text-xs text-gray-600"}>
                  {t("orderPriceAdjustment.startupFeeWarning")}
                </p>
              )}
              {after && (
                <TotalsBlock
                  before={data.totals}
                  after={after}
                  currency={data.currency}
                />
              )}
              <label className={"flex flex-col gap-1 text-sm"}>
                <span className={"text-xs font-semibold text-[#8A93A6]"}>
                  {t("orderPriceAdjustment.note")}
                </span>
                <input
                  type="text"
                  value={note}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={!data.adjustable || busy}
                  onChange={(e) => setNote(e.target.value)}
                  className={
                    "w-full rounded border border-[#C3CAD9] bg-white px-2 py-1 text-sm " +
                    "focus:outline-none focus:ring-2 focus:ring-[#3CB44A] disabled:bg-[#FAFBFE]"
                  }
                  data-testid="adjust-prices-note"
                />
              </label>
              {data.history.length > 0 && (
                <div className={"text-xs text-gray-600"}>
                  <p className={"font-semibold"}>
                    {t("orderPriceAdjustment.historyTitle")}
                  </p>
                  <ul className={"list-disc pl-4"}>
                    {data.history.map((h) => (
                      <li key={h.version}>
                        v{h.version} ·{" "}
                        {formatDate(new Date(parseUtcStamp(h.adjustedAtUtc)))} ·{" "}
                        {h.adjustedBy} · {formatMoney(h.totalBefore, data.currency)}{" "}
                        &rarr; {formatMoney(h.totalAfter, data.currency)}
                        {h.note ? `: ${h.note}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <div className={"flex justify-end gap-4 pt-2"}>
            <SecondaryButton onClick={close} disabled={saving}>
              {t("cancel")}
            </SecondaryButton>
            <PrimaryButton onClick={confirm.open} disabled={!canSave}>
              {t("orderPriceAdjustment.save")}
            </PrimaryButton>
          </div>
        </div>
      </DialogFrame>
      {confirm.component}
    </>
  );
}

function PriceLineRow({
  line,
  currency,
  raw,
  changed,
  disabled,
  onChange,
}: {
  line: OrderPriceAdjustmentLineDto;
  currency: string;
  raw: string;
  // The field differs from the stored agreement (an unsaved edit).
  changed: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const parsed = parsePriceInput(raw);
  const invalid = line.adjustable && !parsed.valid;
  const effective = line.adjustable
    ? effectivePrice(line.ourPrice, parsed.valid ? parsed.value : line.agreedPrice)
    : effectivePrice(line.ourPrice, line.agreedPrice);
  const lockedTitle = line.adjustable
    ? undefined
    : t(notAdjustableKey(line.productType));
  // Three states the admin must be able to tell apart at a glance: no
  // agreement (empty field, "No agreement" placeholder), a stored agreement
  // (bold value + "Agreed" badge) and an unsaved edit (orange border, no
  // badge) - so a typed-but-unsaved number never passes for an agreement.
  const hasStoredAgreement =
    line.agreedPrice !== null && line.agreedPrice !== undefined;
  const showsStoredAgreement = hasStoredAgreement && !changed && !invalid;
  const stateClass = invalid
    ? invalidInputClass
    : changed
      ? changedInputClass
      : showsStoredAgreement
        ? agreedInputClass
        : "";
  return (
    <tr
      className={line.adjustable ? "" : "text-gray-400"}
      title={lockedTitle}
      data-testid={`adjust-prices-line-${line.lineId}`}
    >
      <td className={"px-2 py-1"}>
        <span className={"font-mono text-xs mr-2"}>{line.displayNo}</span>
        <span className={"mr-2"}>{line.productNo}</span>
        <span>{line.productName}</span>
      </td>
      <td className={"px-2 py-1 text-right tabular-nums"}>{line.quantity}</td>
      <td className={"px-2 py-1 text-right tabular-nums"}>
        {formatMoney(line.ourPrice, currency)}
      </td>
      <td className={"px-2 py-1 text-right whitespace-nowrap"}>
        {line.adjustable ? (
          <>
            {showsStoredAgreement && (
              <span
                className={
                  "mr-2 rounded bg-[#3CB44A]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#2E8B3A]"
                }
                data-testid={`agreed-badge-${line.lineId}`}
              >
                {t("orderPriceAdjustment.agreedBadge")}
              </span>
            )}
            <input
              type="text"
              inputMode="decimal"
              value={raw}
              placeholder={t("orderPriceAdjustment.noAgreement")}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              className={inputClass + stateClass}
              aria-invalid={invalid}
              aria-label={`${t("orderPriceAdjustment.agreedPrice")} ${line.displayNo}`}
              title={invalid ? t("orderPriceAdjustment.invalidInput") : undefined}
              data-testid={`agreed-price-${line.lineId}`}
              data-state={
                invalid
                  ? "invalid"
                  : changed
                    ? "changed"
                    : showsStoredAgreement
                      ? "agreed"
                      : "none"
              }
            />
          </>
        ) : (
          <span>&mdash;</span>
        )}
      </td>
      <td className={"px-2 py-1 text-right tabular-nums"}>
        {formatMoney(round2(line.quantity * effective), currency)}
      </td>
    </tr>
  );
}

function TotalsBlock({
  before,
  after,
  currency,
}: {
  before: OrderPriceAdjustmentDto["totals"];
  after: OrderPriceAdjustmentDto["totals"];
  currency: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const rows: [string, number, number][] = [
    ["orderPriceAdjustment.subTotal", before.subTotal, after.subTotal],
    ["orderPriceAdjustment.vat", before.vat, after.vat],
    ["orderPriceAdjustment.total", before.total, after.total],
  ];
  return (
    <table
      className={"ml-auto text-sm tabular-nums"}
      data-testid="adjust-prices-totals"
    >
      <thead className={"text-xs uppercase text-gray-600"}>
        <tr>
          <th></th>
          <th className={"px-2 text-right"}>{t("orderPriceAdjustment.before")}</th>
          <th className={"px-2 text-right"}>{t("orderPriceAdjustment.after")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([key, b, a], i) => (
          <tr key={key} className={i === rows.length - 1 ? "font-bold" : ""}>
            <td className={"px-2 text-right"}>{t(key)}</td>
            <td className={"px-2 text-right"}>{formatMoney(round2(b), currency)}</td>
            <td
              className={
                "px-2 text-right" +
                (round2(a) !== round2(b) ? " text-orange-700" : "")
              }
            >
              {formatMoney(round2(a), currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
